import { useRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconGear } from '@components/icons/gear';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { Modal, hideModal, useModal } from '@components/modal';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AvailableJourney, TriggerType } from './api';
import { useAvailableJourneys } from './hooks';

import { JourneyCard } from '../components/journey-card';
import { JourneyCardList } from '../components/journey-card-list';
import { JOURNEYS_LIST_CAP } from '../components/journey-search';
import { JourneyListEmpty, JourneyListError, JourneyListLoading } from '../components/list-states';
import { journeyListStrings } from '../components/list-strings';

export type JourneySelectorModalProps = {
  jobId: number;
  triggerType: TriggerType;
  jobStage?: number | null;
  isAdmin: boolean;
  onSelect: (journey: AvailableJourney) => void;
};

const TRIGGER_LABEL_KEYS = {
  stage: 'triggerStage',
  hired: 'triggerHired',
  rejected: 'triggerRejected',
} as const;

export const useJourneySelectorModal = () => {
  return useModal(JourneySelectorModal, { variant: 'REGULAR' });
};

const JourneySelectorModal = ({
  jobId,
  triggerType,
  jobStage,
  isAdmin,
  onSelect,
}: JourneySelectorModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');
  const journeysQuery = useAvailableJourneys(jobId, search);

  // The API returns the journeys already sorted with the shared deterministic
  // key every surface uses, and already filtered server-side by `search`.
  const filteredJourneys = journeysQuery.data?.results ?? [];

  const hasSelectedRef = useRef(false);
  const handleSelect = (journey: AvailableJourney) => {
    // A double-click would fire the mutation twice; the second POST 400s as a
    // duplicate and surfaces a bogus error toast right after a successful add.
    if (hasSelectedRef.current) {
      return;
    }
    hasSelectedRef.current = true;
    onSelect(journey);
    hideModal();
  };

  const triggerLabel =
    triggerType === 'stage' && jobStage == null ? null : t(TRIGGER_LABEL_KEYS[triggerType]);

  return (
    <Modal>
      <Modal.Header>
        <HeaderRow>
          <Stack orientation="horizontal" align="center" space={3}>
            <HeaderIconTile>
              <IconJourneyItem size={20} color="blue100" />
            </HeaderIconTile>
            <Modal.HeaderTitle title={t('title')} />
          </Stack>
          <Stack orientation="horizontal" align="center" space={2}>
            {isAdmin && (
              <Button
                dataCy="manage-journeys-link"
                variant="ghost"
                iconLeft={<IconGear />}
                iconRight={<IconOpenNewTab size={16} />}
                text={t('manage')}
                href="/settings/apps/journeys"
                target="_blank"
              />
            )}
            <Button
              variant="ghost"
              iconLeft={<IconCloseRegular />}
              onClick={hideModal}
              tooltip={t('closeTooltip')}
              aria-label={t('close')}
            />
          </Stack>
        </HeaderRow>
      </Modal.Header>
      <Modal.Content data-cy="journey-selector-modal">
        <Stack space={4}>
          {triggerLabel && <Text font="regular14" color="textSecondary" text={triggerLabel} />}
          <SearchBox
            dataTestid="journey-selector-search"
            placeholder={t('searchPlaceholder')}
            defaultValue=""
            onChange={setSearch}
            onChangeDebounce={300}
          />
          {journeysQuery.data?.isTruncated && (
            <Text
              font="regular12"
              color="textHelper"
              text={t('showingFirstResults', { count: String(JOURNEYS_LIST_CAP) })}
            />
          )}
          {journeysQuery.isLoading ? (
            <JourneyListLoading />
          ) : journeysQuery.isError ? (
            <JourneyListError
              description={t('loadError')}
              retryLabel={t('retry')}
              onRetry={() => journeysQuery.refetch()}
            />
          ) : filteredJourneys.length === 0 ? (
            // An empty allow-list and a search that matched nothing need
            // different answers: opt a journey in, versus clear the search.
            <JourneyListEmpty text={search.trim() ? t('noResults') : t('noJourneys')} />
          ) : (
            <JourneyCardList
              items={filteredJourneys}
              getKey={(journey) => journey.journeyId}
              renderItem={(journey) => (
                <JourneyCard
                  dataCy="journey-selector-item"
                  title={journey.title}
                  description={journey.description}
                  fieldsCount={journey.fieldsCount}
                  preFilledCount={journey.preFilledCount}
                  onClick={() => handleSelect(journey)}
                />
              )}
            />
          )}
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const HeaderIconTile = styled.div({
  width: pxToRem(32),
  height: pxToRem(32),
  borderRadius: pxToRem(8),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: theme.color.uiTrans5,
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
});

const locale = {
  en: {
    ...journeyListStrings.en,
    title: 'Select journey',
    searchPlaceholder: 'Search by name, description or workspace...',
    noJourneys: 'No journeys available',
    retry: 'Retry',
    manage: 'Manage',
    close: 'Close',
    // Icon-only control: aria-label stays the short accessible name;
    // the tooltip carries the longer description, so a screen-reader user
    // does not hear the identical text twice (once as the name, once again
    // as the tooltip on focus).
    closeTooltip: 'Close journey selector',
    triggerHired: 'Select a journey to trigger when an applicant is hired',
    triggerRejected: 'Select a journey to trigger when an applicant is rejected',
    triggerStage: 'Select a journey to trigger when an applicant enters this pipeline stage',
  },
  is: {
    ...journeyListStrings.is,
    title: 'Veldu Journey-ferli',
    searchPlaceholder: 'Leita eftir nafni, lýsingu eða vinnusvæði…',
    noJourneys: 'Engin Journey-ferli í boði',
    retry: 'Reyna aftur',
    manage: 'Umsjón',
    close: 'Loka',
    closeTooltip: 'Loka glugganum fyrir val á Journey-ferli',
    triggerHired: 'Veldu Journey-ferli sem ræsist þegar umsækjandi er ráðinn',
    triggerRejected: 'Veldu Journey-ferli sem ræsist þegar umsækjanda er hafnað',
    triggerStage: 'Veldu Journey-ferli sem ræsist þegar umsækjandi er færður í þennan dálk',
  },
};
